import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { styled } from '@mui/material/styles';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { GoogleMap, Marker, useLoadScript } from '@react-google-maps/api'
import { useLogoTitle } from '../classes/LogoTitleContext';
import Header from '../Parts/Header';
import Footer from '../Parts/Footer';
import Sidebar from '../Parts/Sidebar';
import vacuumIcon from '../Images/MapIcons/vacuum.png';
import greenPotIcon from '../Images/MapIcons/green.png';
import redPotIcon from '../Images/MapIcons/red.png';
import orangePotIcon from '../Images/MapIcons/orange.png';
import refreshIcon from '../Images/refresh-icon.svg';
import getAuthHeaders from '../Auth/Authentication';
import { API_ENDPOINTS } from '../Auth/apiConfig';
import axios from 'axios';
import { Typography } from '@mui/material';
import Box from '@mui/material/Box';

const ContentContainer = styled('div')({
    position: 'relative',
    minHeight: 'calc(100vh - 64px)',
    paddingBottom: '100px',
    overflowY: 'auto',
    marginLeft: 90,
    '@media (max-width: 900px)': {
        marginLeft: 65,
    }
});

const MainContent = styled('div')({
    padding: '20px',
    marginTop: '80px',
    marginBottom: '10px',
    position: 'relative',
    '@media (max-width: 768px)': {
        padding: '10px',
    },
});

const RefreshIcon = styled('img')({
    cursor: 'pointer',
    width: '40px',
    height: '40px',
    transition: 'transform 0.3s ease-in-out',
    '&:hover': {
        transform: 'rotate(360deg)',
    },
    '@media (max-width: 600px)': {
        width: '30px',
        height: '30px',
    }
});

const Tooltip = styled('div')({
    position: 'absolute',
    backgroundColor: 'white',
    padding: '10px',
    border: '2px solid dodgerblue',
    borderRadius: '4px',
    boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
    fontSize: '12px', // Default font size
    whiteSpace: 'nowrap',
    zIndex: 1000,
    top: '15%',
    right: '5%',
    maxWidth: '400px',
    overflow: 'auto',

    // Responsive adjustments
    '@media (max-width: 768px)': {
        fontSize: '10px', // Smaller font size on medium screens
        padding: '8px', // Reduced padding
        maxWidth: '300px', // Decrease max width on medium screens
        top: '10%', // Adjust position for medium screens
        right: '3%', // Adjust position for medium screens
    },
    '@media (max-width: 480px)': {
        fontSize: '9px', // Smaller font size on small screens
        padding: '6px', // Further reduced padding
        maxWidth: '250px', // Further decrease max width on small screens
        top: '5%', // Adjust position for small screens
        right: '2%', // Adjust position for small screens
    }
});


const ResponsiveMapContainer = styled('div')({
    marginTop: '1%',
    border: '2px solid dodgerblue',
    borderRadius: '8px',
    overflow: 'hidden',
    width: '90vw',
    maxWidth: '100%',
    height: '65vh',

    // Responsive adjustments for different screen sizes
    '@media (max-width: 1200px)': {
        width: '85vw', // Slightly wider on medium screens
        height: '60vh', // Reduce height on medium screens
    },
    '@media (max-width: 900px)': {
        width: '95vw', // Full width on small screens
        height: '55vh', // Further reduce height on small screens
    },
    '@media (max-width: 600px)': {
        width: '100vw', // Full width on extra small screens
        height: '50vh', // Reduce height for extra small screens
    },
    '@media (max-width: 480px)': {
        width: '100%', // Ensure full width on very small screens
        height: '45vh', // Further reduce height for very small screens
        marginTop: '2%', // Add a bit more margin on very small screens
    },
});


const HeaderTitle = styled(Typography)({
    flexGrow: 1,
    fontSize: '1.4rem',
    color: '#333',
    fontWeight: 'normal',
    textAlign: 'left',
    textShadow: '2px 2px 4px rgba(0, 0, 0, 0.3)',
});

const Map = ({ onLogout, userData, loggedInUser }) => {
    const navigate = useNavigate();
    const [isLoggedIn, setIsLoggedIn] = useState();
    const [username, setUsername] = useState();
    const [userRole, setUserRole] = useState('');
    const { logoUrl, projectTitle } = useLogoTitle();
    const [vacuumStations, setVacuumStations] = useState([]);
    const [potData, setPotData] = useState([]);
    const [mapCenter, setMapCenter] = useState({ lat: 0, lng: 0 });
    const [mapZoom, setMapZoom] = useState(2);
    const [tooltipData, setTooltipData] = useState(null);
    const [tooltipType, setTooltipType] = useState('');

    // // For Local Development
    // const { isLoaded, loadError } = useLoadScript({
    //     googleMapsApiKey: 'AIzaSyD9zUUA7KUblHI9QOPyO7NnEvoVKHqeSvE',
    // });

    // For Production
    const { isLoaded, loadError } = useLoadScript({
        googleMapsApiKey: 'AIzaSyCaP8eLp-zATiVaYNib4gP3UccqaLwtJRo',
    });

    const notify = (message) => toast.info(message, {
        css: {
            fontSize: '16px',
            '@media (max-width: 600px)': {
                fontSize: '14px',
            },
        },
    });

    const fetchVacuumStations = async () => {
        try {
            //console.log('Fetching vacuum stations...');
            const authHeaders = getAuthHeaders();
            const response = await axios.get(API_ENDPOINTS.StationBallonLocations, {
                params: { clientId: loggedInUser.clientId },
                headers: authHeaders
            });

            //console.log('Response received:', response);
            //console.log('Data:', response.data);

            setVacuumStations(response.data);

            if (response.data.length > 0) {
                //console.log('First station data:', response.data[0]);
                setMapCenter({
                    lat: response.data[0].locLongitude,
                    lng: response.data[0].locLattitude
                });
                setMapZoom(15);
            }
        } catch (error) {
            console.error('Error fetching vacuum stations:', error);
            //console.log('Error details:', error.response ? error.response.data : error.message);
        }
    };

    const fetchPotLocData = async () => {
        try {
            //console.log('Fetching pot location data...');
            const authHeaders = getAuthHeaders();
            const response = await axios.get(API_ENDPOINTS.PotBallonLocations, {
                params: { clientId: loggedInUser.clientId },
                headers: authHeaders
            });

            //console.log('Response received for PotData:', response);
            //console.log('Data:', response.data);

            // Process data to filter based on type priority
            const filteredPotData = response.data.reduce((acc, curr) => {
                const { pot, type } = curr;

                // If pot does not exist in the accumulator, add it
                if (!acc[pot]) {
                    acc[pot] = curr;
                } else {
                    if (type === 'red') {
                        acc[pot] = curr;
                    } else if (type === 'green' && acc[pot].type === 'green') {
                        acc[pot] = curr;
                    }
                }

                return acc;
            }, {});

            // Set the processed data
            const finalPotData = Object.values(filteredPotData);
            setPotData(finalPotData);

            if (finalPotData.length > 0) {
                //console.log('Pot Data:', finalPotData[0]);
                setMapCenter({
                    lat: finalPotData[0].locLongitude,
                    lng: finalPotData[0].locLattitude
                });
                setMapZoom(15);
            }
        } catch (error) {
            console.error('Error fetching pot location data:', error);
            console.log('Error details:', error.response ? error.response.data : error.message);
        }
    };


    useEffect(() => {
        if (loggedInUser?.clientId && isLoaded) {
            fetchVacuumStations();
            fetchPotLocData();
        }
    }, [loggedInUser, isLoaded]);

    const handleMarkerMouseOver = useCallback((e, marker, type) => {
        setTooltipData(marker);
        setTooltipType(type);
    }, []);

    const handleMarkerMouseOut = useCallback(() => {
        setTooltipData(null);
        setTooltipType('');
    }, []);

    // Function to handle marker click
    const handleMarkerClick = (pot) => {
        if (loggedInUser && (loggedInUser.clientId == 509 || loggedInUser.clientId == 510)) {
            // Do nothing, stay on same page
            return;
        }

        // Navigate to PotOverview page with the correct pot ID
        navigate(`/MapOverview/${pot.pot}`);
    };

    if (loadError) return <div>Error loading map</div>;
    if (!isLoaded) return <div>Loading Map...</div>;
    if (!loggedInUser || !loggedInUser.clientId) return <div>Loading...</div>;

    return (
        <div style={{ backgroundColor: '#EBEFF3', minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
            <Sidebar loggedInUser={loggedInUser} />
            <Header
                logoUrl={logoUrl}
                loggedInUser={loggedInUser}
                onLogout={onLogout}
                username={username}
                userRole={userRole}
            />
            <ContentContainer>
                <MainContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '-15px' }}>
                        <HeaderTitle>Map</HeaderTitle>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <RefreshIcon
                                title="Refresh"
                                src={refreshIcon}
                                alt="Refresh"
                                onClick={() => window.location.reload()}
                            />
                        </Box>
                    </Box>
                    <ResponsiveMapContainer>
                        <GoogleMap
                            mapContainerStyle={{ width: '100%', height: '100%' }}
                            center={mapCenter}
                            zoom={mapZoom}
                            options={{
                                mapTypeId: 'hybrid',
                                zoomControl: true,
                                streetViewControl: false,
                                mapTypeControl: false,
                                fullscreenControl: true,
                                styles: [
                                    {
                                        featureType: 'poi',
                                        elementType: 'labels.icon',
                                        stylers: [{ visibility: 'off' }],
                                    },
                                    {
                                        featureType: 'transit',
                                        elementType: 'labels.icon',
                                        stylers: [{ visibility: 'off' }],
                                    },
                                ],
                            }}
                        >
                            {vacuumStations.map((station, index) => (
                                <Marker
                                    key={index}
                                    position={{ lat: station.locLongitude, lng: station.locLattitude }}
                                    icon={{
                                        url: vacuumIcon,
                                        scaledSize: new window.google.maps.Size(20, 30)
                                    }}
                                    onMouseOver={(e) => handleMarkerMouseOver(e, station, 'vacuum')}
                                    onMouseOut={handleMarkerMouseOut}
                                />
                            ))}
                            {potData.map((pot, index) => (
                                <Marker
                                    key={index}
                                    position={{ lat: pot.locLongitude, lng: pot.locLattitude }}
                                    icon={{
                                        url: pot.type === 'green' ? greenPotIcon :
                                            pot.type === 'red' ? redPotIcon :
                                                pot.type === 'orange' ? orangePotIcon : greenPotIcon,
                                        scaledSize: new window.google.maps.Size(30, 30)
                                    }}
                                    onMouseOver={(e) => handleMarkerMouseOver(e, pot, 'pot')}
                                    onMouseOut={handleMarkerMouseOut}
                                    onClick={() => handleMarkerClick(pot)}
                                />
                            ))}
                        </GoogleMap>
                    </ResponsiveMapContainer>
                    {tooltipData && (
                        <Tooltip>
                            <div>
                                {tooltipType === 'vacuum' && (
                                    <>
                                        <h4 style={{ margin: '0', fontSize: '16px' }}>City: {tooltipData.addrCity}</h4>
                                        <p style={{ margin: '0', fontSize: '14px' }}>Location: {tooltipData.addrStreet}</p>
                                        <p style={{ margin: '0', fontSize: '14px' }}>Last updated on: {tooltipData.AdjustedLastUpdateOn}</p>
                                    </>
                                )}
                                {tooltipType === 'pot' && (
                                    <>
                                        <h4 style={{ margin: '0', fontSize: '16px' }}>Pot ID: {tooltipData.pot}</h4>
                                        <p style={{ margin: '0', fontSize: '14px' }}>Location: {tooltipData.addrStreet}</p>
                                        {loggedInUser && !(loggedInUser.clientId == 509 || loggedInUser.clientId == 510) && (
                                            <>
                                                <p style={{ margin: '0', fontSize: '14px' }}>Sensor address: {tooltipData.sensorAddress}</p>
                                                <p style={{ margin: '0', fontSize: '14px' }}>Master module: {tooltipData.masterModule}</p>
                                            </>
                                        )}
                                        <p style={{ margin: '0', fontSize: '14px' }}>Last communication: {tooltipData.AdjustedLastUpdateOn}</p>
                                    </>
                                )}
                            </div>
                        </Tooltip>
                    )}
                    <Typography
                        style={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            gap: '16px',
                            backgroundColor: '#f0f0f0',
                            padding: '10px',
                            borderRadius: '8px',
                            marginTop: '10px',
                            flexWrap: 'wrap', // Allow items to wrap on smaller screens
                        }}
                    >
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <img src={vacuumIcon} alt="Vacuum Icon" style={{ width: 20, height: 30 }} />
                            <span style={{ color: '#286FAB', fontWeight: 'bold', fontSize: '14px' }}>Vacuum Station</span>
                        </div>

                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <img src={greenPotIcon} alt="Green Pot Icon" style={{ width: 26, height: 24 }} />
                            <span style={{ color: 'green', fontWeight: 'bold', fontSize: '14px' }}>OK</span>
                        </div>

                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <img src={redPotIcon} alt="Red Pot Icon" style={{ width: 26, height: 24 }} />
                            <span style={{ color: 'red', fontWeight: 'bold', fontSize: '14px' }}>Alarm</span>
                        </div>

                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <img src={orangePotIcon} alt="Orange Pot Icon" style={{ width: 26, height: 24 }} />
                            <span style={{ color: '#FF5E00', fontWeight: 'bold', fontSize: '14px' }}>Not in communication</span>
                        </div>
                    </Typography>
                </MainContent>
            </ContentContainer>
            <Footer />
            <ToastContainer position="top-right" autoClose={5000} hideProgressBar={false} />
        </div>
    );
};

export default Map;